import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Share2, Download, Link, Linkedin, X } from 'lucide-react';
import { BehavioralFactor, PersonalityProfile } from '@/types/resultsTypes';
import { useToast } from '@/hooks/use-toast';

interface ShareProfileModalProps {
  open: boolean;
  onClose: () => void;
  personalityProfile: PersonalityProfile;
  behavioralFactors: BehavioralFactor[];
  userName?: string;
  shareUrl?: string;
}

const TRAIT_COLORS: Record<string, string> = {
  dominance: '#1a1a1a',
  extraversion: '#2563eb',
  patience: '#16a34a',
  formality: '#7c3aed',
  objectivity: '#dc2626',
};

const ShareCard = React.forwardRef<HTMLDivElement, {
  personalityProfile: PersonalityProfile;
  topTraits: BehavioralFactor[];
  userName?: string;
}>(({ personalityProfile, topTraits, userName }, ref) => (
  <div
    ref={ref}
    style={{
      width: '600px',
      minHeight: '315px',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
      borderRadius: '16px',
      padding: '40px',
      fontFamily: "'Inter', sans-serif",
      color: '#ffffff',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    {/* Background accent */}
    <div style={{
      position: 'absolute', top: -60, right: -60,
      width: 200, height: 200,
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.04)',
    }} />

    {/* Zentrix Insights logo mark */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
      <div style={{ fontSize: 11, letterSpacing: '0.15em', color: '#888', textTransform: 'uppercase' }}>
        Zentrix Insights
      </div>
      {userName && (
        <div style={{ fontSize: 13, color: '#aaa' }}>{userName}</div>
      )}
    </div>

    {/* Personality type */}
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
        {personalityProfile.type}
      </div>
      <div style={{ fontSize: 14, color: '#999', marginTop: 6, maxWidth: 460 }}>
        {personalityProfile.description}
      </div>
    </div>

    {/* Divider */}
    <div style={{ width: 40, height: 2, background: '#444', borderRadius: 2, margin: '20px 0' }} />

    {/* Top 3 traits */}
    <div style={{ display: 'flex', gap: 12 }}>
      {topTraits.map((trait) => (
        <div key={trait.key} style={{
          flex: 1,
          background: 'rgba(255,255,255,0.06)',
          borderRadius: 10,
          padding: '14px 16px',
          borderTop: `3px solid ${TRAIT_COLORS[trait.key] || '#555'}`,
        }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>
            {trait.percentile}%
          </div>
          <div style={{ fontSize: 11, color: '#aaa', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {trait.name}
          </div>
        </div>
      ))}
    </div>

    {/* Footer */}
    <div style={{ marginTop: 24, fontSize: 11, color: '#555', letterSpacing: '0.05em' }}>
      zentrixinsights.com
    </div>
  </div>
));

ShareCard.displayName = 'ShareCard';

const ShareProfileModal: React.FC<ShareProfileModalProps> = ({
  open,
  onClose,
  personalityProfile,
  behavioralFactors,
  userName,
  shareUrl,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const { toast } = useToast();

  // Top 3 traits by percentile
  const topTraits = [...behavioralFactors]
    .sort((a, b) => b.percentile - a.percentile)
    .slice(0, 3);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
      });
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `zentrix-profile-${personalityProfile.type.toLowerCase().replace(/\s+/g, '-')}.png`;
      a.click();
      toast({ title: 'Downloaded!', description: 'Your profile card is ready to share.' });
    } catch (err) {
      toast({ title: 'Download failed', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyLink = async () => {
    const url = shareUrl || window.location.href;
    await navigator.clipboard.writeText(url);
    toast({ title: 'Link copied!', description: 'Share your results with anyone.' });
  };

  const handleLinkedIn = () => {
    const url = encodeURIComponent(shareUrl || window.location.href);
    const text = encodeURIComponent(
      `I just completed my Zentrix Insights personality assessment — I'm a "${personalityProfile.type}". Curious what yours says? 🔍`
    );
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}&summary=${text}`,
      '_blank',
      'width=600,height=600'
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Share Your Profile
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Preview card */}
          <div className="flex justify-center overflow-hidden rounded-xl">
            <ShareCard
              ref={cardRef}
              personalityProfile={personalityProfile}
              topTraits={topTraits}
              userName={userName}
            />
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-3 gap-3">
            <Button
              variant="outline"
              onClick={handleLinkedIn}
              className="flex items-center gap-2"
            >
              <Linkedin className="w-4 h-4 text-[#0077B5]" />
              LinkedIn
            </Button>
            <Button
              variant="outline"
              onClick={handleCopyLink}
              className="flex items-center gap-2"
            >
              <Link className="w-4 h-4" />
              Copy Link
            </Button>
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-2 bg-black text-white hover:bg-gray-800"
            >
              <Download className="w-4 h-4" />
              {downloading ? 'Saving...' : 'Download'}
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Download the card image and attach it to your LinkedIn post for best results.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareProfileModal;
